/**
 * Support inbox (D79), for staff with "Answer support chats". List on the left, the open chat on
 * the right (stacked on phones). Polls every 3 s for the open chat and every 10 s for the list
 * (D75). Replies, assign and close need no 2FA code (everyday work) but assign/close are audited.
 */
import type { AdminChatRow } from "@custos/api-client";
import {
  Badge,
  Button,
  Card,
  ChatComposer,
  ChatImage,
  ChatThread,
  EmptyState,
  Notice,
  Segmented,
  Skeleton,
  Spot,
  cn,
  formatDateTime,
} from "@custos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { PageHeader } from "../components/AdminShell";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { useCan } from "../lib/permissions";
import { useAdminMe } from "../lib/queries";

type Filter = "UNASSIGNED" | "MINE" | "OPEN" | "CLOSED";

export function SupportPage() {
  const { id } = useParams();
  const [filter, setFilter] = useState<Filter>("OPEN");
  const inbox = useQuery({
    queryKey: ["support", filter],
    queryFn: () => api.supportInbox(filter),
    refetchInterval: 10_000,
  });

  return (
    <>
      <PageHeader
        title="Support"
        subtitle="Live chat with customers. Never ask for recovery phrases, keys, passwords or codes."
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <section aria-label="Conversations" className={cn("flex flex-col gap-3", id && "hidden lg:flex")}>
          <Segmented
            label="Which conversations"
            value={filter}
            onChange={setFilter}
            options={[
              { value: "OPEN", label: "Open" },
              { value: "UNASSIGNED", label: "New" },
              { value: "MINE", label: "Mine" },
              { value: "CLOSED", label: "Closed" },
            ]}
          />
          {inbox.error && <Notice tone="danger">{errorMessage(inbox.error)}</Notice>}
          {inbox.isPending ? (
            <Skeleton className="h-40 rounded-card" />
          ) : inbox.data?.length === 0 ? (
            <Card>
              <EmptyState icon={<Spot name="history" />} title="No conversations here" />
            </Card>
          ) : (
            <ul className="flex flex-col gap-2" data-testid="support-inbox">
              {inbox.data?.map((c) => (
                <InboxRow key={c.id} row={c} active={c.id === id} />
              ))}
            </ul>
          )}
        </section>
        <section aria-label="Conversation" className={cn(!id && "hidden lg:block")}>
          {id ? (
            <Conversation id={id} />
          ) : (
            <Card>
              <EmptyState icon={<Spot name="history" />} title="Pick a conversation" />
            </Card>
          )}
        </section>
      </div>
    </>
  );
}

function InboxRow({ row, active }: { row: AdminChatRow; active: boolean }) {
  return (
    <li>
      <Link
        to={`/support/${row.id}`}
        data-testid={`inbox-${row.userEmail}`}
        className={cn(
          "flex flex-col gap-1 rounded-[20px] border p-3 transition-colors",
          active ? "border-primary bg-soft" : "border-line bg-surface hover:bg-hover",
        )}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-fg">{row.userEmail}</span>
          {row.unread > 0 && <Badge tone="primary">{row.unread} new</Badge>}
        </span>
        <span className="truncate text-sm text-muted">
          {row.lastSender === "STAFF" ? "You: " : ""}
          {row.preview}
        </span>
        <span className="text-xs text-muted">
          {formatDateTime(row.lastMessageAt)} · {row.assignedName ?? "unassigned"}
          {row.status === "CLOSED" ? " · closed" : ""}
        </span>
      </Link>
    </li>
  );
}

function Conversation({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const me = useAdminMe();
  const can = useCan();
  const chat = useQuery({
    queryKey: ["support-chat", id],
    queryFn: () => api.supportChat(id),
    refetchInterval: 3_000,
  });
  const lastId = chat.data?.messages.at(-1)?.id;

  // Opening (and every new message while open) marks the chat as read for staff.
  useEffect(() => {
    if (!lastId) return;
    void api.markSupportRead(id).then(() => queryClient.invalidateQueries({ queryKey: ["support-unread"] }));
  }, [id, lastId, queryClient]);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["support-chat", id] }),
      queryClient.invalidateQueries({ queryKey: ["support"] }),
    ]);
  const action = useMutation({
    mutationFn: (kind: "assign" | "unassign" | "close" | "reopen") =>
      kind === "assign"
        ? api.assignSupport(id, true)
        : kind === "unassign"
          ? api.assignSupport(id, false)
          : kind === "close"
            ? api.closeSupport(id)
            : api.reopenSupport(id),
    onSuccess: refresh,
  });

  if (chat.isPending) return <Skeleton className="h-64 rounded-card" />;
  if (chat.error) return <Notice tone="danger">{errorMessage(chat.error)}</Notice>;
  const c = chat.data.conversation;
  const open = c.status === "OPEN";
  const mine = c.assignedTo === me.data?.id;

  return (
    <Card className="flex flex-col gap-4" data-testid="support-conversation">
      <button
        type="button"
        onClick={() => navigate("/support")}
        className="-mt-1 inline-flex min-h-11 items-center gap-1 self-start text-sm text-muted hover:text-fg lg:hidden"
      >
        <ArrowLeft className="size-4" aria-hidden /> All conversations
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-fg">
            {can("users.view") ? (
              <Link to={`/users/${c.user.id}`} className="underline-offset-4 hover:underline">
                {c.user.email}
              </Link>
            ) : (
              c.user.email
            )}
          </p>
          <p className="text-xs text-muted">
            Started {formatDateTime(c.createdAt)} · {c.assignedName ? `with ${c.assignedName}` : "unassigned"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge tone={open ? "success" : "neutral"}>{open ? "open" : "closed"}</Badge>
          {open && (
            <Button
              size="sm"
              variant="secondary"
              loading={action.isPending}
              onClick={() => action.mutate(mine ? "unassign" : "assign")}
            >
              {mine ? "Unassign me" : "Assign to me"}
            </Button>
          )}
          <Button
            size="sm"
            variant={open ? "danger" : "secondary"}
            loading={action.isPending}
            onClick={() => action.mutate(open ? "close" : "reopen")}
          >
            {open ? "Close" : "Reopen"}
          </Button>
        </div>
      </div>
      {action.error && <Notice tone="danger">{errorMessage(action.error)}</Notice>}
      <p className="flex items-start gap-2 rounded-field border border-warning/30 bg-warning/5 p-3 text-xs text-muted">
        <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
        Never ask for a recovery phrase, private key, password or 2FA code. If a customer offers one, tell
        them to stop and to move their funds to a new wallet if they already shared it anywhere.
      </p>
      <ChatThread
        me="STAFF"
        messages={chat.data.messages}
        renderImage={(attId) => <ChatImage load={() => api.supportImage(attId)} />}
        className="max-h-[55dvh] min-h-48 overflow-y-auto pr-1"
      />
      {open ? (
        <ChatComposer
          placeholder={`Reply as ${me.data?.displayName ?? "Aurix Support"}…`}
          onSend={async (input) => {
            try {
              await api.sendSupport(id, input);
            } catch (e) {
              throw new Error(errorMessage(e), { cause: e });
            }
            await refresh();
          }}
        />
      ) : (
        <p className="text-center text-sm text-muted">This conversation is closed. Reopen it to reply.</p>
      )}
    </Card>
  );
}
